// ============================================================================
// JARVISTRAVEL - OFFLINE GUARD (JAR-314 / L3)
// A declarative wrapper that pauses backend-dependent actions while
// unreachable: renders an OfflineNotice and disables the child button.
// Replaces the manual useBackendStatus + OfflineNotice + disabled-OR pattern
// with a one-line wrapper, making per-screen degradation consistent and
// ensuring new screens get it right by default.
//
// Usage:
//   <OfflineGuard message="Saving is paused…" disabled={!name.trim()}>
//     <Button>Save</Button>
//   </OfflineGuard>
// ============================================================================

import { cloneElement } from 'react';
import { useBackendStatus } from '@/app/hooks';
import { OfflineNotice } from './OfflineNotice';
import type { ReactElement } from 'react';

interface OfflineGuardProps {
  /** The action button/trigger to guard. Its disabled prop is OR'd with offline. */
  children: ReactElement<{ disabled?: boolean }>;
  /** Surface-specific copy passed through to OfflineNotice. */
  message?: string;
  /** Additional conditions that should disable the button (OR'd with offline). */
  disabled?: boolean;
  className?: string;
}

export function OfflineGuard({ children, message, disabled = false, className = '' }: OfflineGuardProps) {
  const backend = useBackendStatus();
  const isOffline = backend === 'offline';

  return (
    <div className={className}>
      {isOffline && <OfflineNotice message={message} />}
      {cloneElement(children, {
        disabled: children.props.disabled || disabled || isOffline,
      })}
    </div>
  );
}

export default OfflineGuard;
